import React, { useState, useEffect, useRef } from "react";
import { database, auth } from "../firebaseConfig";
import { ref, update, onValue } from "firebase/database";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  writeBatch,
  getCountFromServer,
  getDoc,
  setDoc
} from "firebase/firestore";
import { firestore } from "../firebaseConfig";
import WhisperUploader from "./WhisperUploader";
import "./WhisperSettings.css";

const WhisperSettings = ({ userId }) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingAll, setDeletingAll] = useState(false);
  const [showDatabasePopup, setShowDatabasePopup] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showProfileDetails, setShowProfileDetails] = useState(false);
  const [showUploader, setShowUploader] = useState(false);
  const [showGeneralSettings, setShowGeneralSettings] = useState(false);
  const [profileNumber, setProfileNumber] = useState(10);
  const [smartAddEnabled, setSmartAddEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const databasePathRef = useRef(null);
  const [collapsedSections, setCollapsedSections] = useState({
    general: false,
    profiles: false
  });
  
  // Database URL and path for this user
  const databaseURL = "https://bonusclub-ab5e2-default-rtdb.europe-west1.firebasedatabase.app";
  const databasePath = `/Whisper/${userId}`;
  const fullDatabasePath = `${databaseURL}${databasePath}`;
  
  // Function to copy user ID to clipboard
  const copyUserId = () => {
    if (databasePathRef.current) {
      databasePathRef.current.select();
      document.execCommand('copy');
      setCopySuccess("User ID copied to clipboard!");
      setTimeout(() => setCopySuccess(""), 2000);
    }
  };
  
  // Toggle database popup
  const toggleDatabasePopup = () => {
    setShowDatabasePopup(!showDatabasePopup);
    setCopySuccess("");
  };
  
  // Close database popup when clicked outside
  const handlePopupOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowDatabasePopup(false);
    }
  };
  
  // Toggle section collapse
  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };
  
  // Show profile details
  const showProfileDetail = (profile) => {
    setSelectedProfile(profile);
    setShowProfileDetails(true);
  };
  
  // Close profile details
  const closeProfileDetails = () => {
    setSelectedProfile(null);
    setShowProfileDetails(false);
  };
  
  // Toggle uploader
  const toggleUploader = () => {
    setShowUploader(!showUploader);
  };

    // Load settings function
  const loadSettings = async () => {
    if (!userId) return;
    
    try {
      const settingsRef = doc(firestore, `Whisper/${userId}/settings`, 'general');
      const snapshot = await getDoc(settingsRef);
      
      if (snapshot.exists()) {
        const settings = snapshot.data();
        if (settings.profileNumber !== undefined) {
          setProfileNumber(settings.profileNumber);
        }
        if (settings.smartAddEnabled !== undefined) {
          setSmartAddEnabled(settings.smartAddEnabled);
        }
      }
    } catch (error) {
      console.error("Error loading settings:", error);
    }
  };

  const toggleGeneralSettings = () => {
    if (!showGeneralSettings) {
      // Load current settings when opening modal
      loadSettings();
    }
    setShowGeneralSettings(!showGeneralSettings);
  };

  // Load existing Instagram profiles
  useEffect(() => {
    if (!userId) return;
    
    const profilesRef = collection(firestore, `Whisper/${userId}/profiles`);
    const unsubscribe = onSnapshot(profilesRef, (snapshot) => {
      if (!snapshot.empty) {
        const profilesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProfiles(profilesData);
      } else {
        setProfiles([]);
      }
      setLoading(false);
    }, (error) => {
      console.error("Error loading profiles:", error);
      setLoading(false);
    });

    // Load initial settings
    loadSettings();
    
    return () => unsubscribe();
  }, [userId]);

  // Function to save settings
  const handleSaveSettings = async () => {
    if (!userId) return;
    
    try {
      setSaving(true);
      
      // Save settings
      const settingsRef = doc(firestore, `Whisper/${userId}/settings`, 'general');
      await setDoc(settingsRef, {
        profileNumber: profileNumber,
        smartAddEnabled: smartAddEnabled
      });
      
      setMessage("Settings saved successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error saving settings:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Function to delete a profile
  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm("Are you sure you want to delete this profile?")) return;

    try {
      const profileRef = doc(firestore, `Whisper/${userId}/profiles`, profileId);
      await deleteDoc(profileRef);
      setMessage("Profile deleted successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("Error deleting profile:", error);
      setMessage(`Error: ${error.message}`);
    }
  };

  // Function to delete all profiles
  const handleDeleteAllProfiles = async () => {
    if (!window.confirm("WARNING: This will delete ALL Instagram profiles. This action cannot be undone. Are you sure?")) return;
    
    // Double confirmation for critical action
    if (!window.confirm("Please confirm again that you want to delete ALL profiles.")) return;
    
    try {
      setDeletingAll(true);
      
      // Delete all profiles using batch operation
      const profilesRef = collection(firestore, `Whisper/${userId}/profiles`);
      const snapshot = await getDocs(profilesRef);
      
      if (!snapshot.empty) {
        const batch = writeBatch(firestore);
        snapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }
      
      setMessage("All profiles have been deleted successfully!");
      setTimeout(() => setMessage(""), 5000);
    } catch (error) {
      console.error("Error deleting all profiles:", error);
      setMessage(`Error: ${error.message}`);
    } finally {
      setDeletingAll(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return "Invalid date";
    }
  };

  // Function to open Instagram profile
  const openInstagramProfile = (profileUrl) => {
    window.open(profileUrl, '_blank', 'noopener,noreferrer');
  };

  // Function to standardize profile link if old format is used
  const getProfileUrl = (profile) => {
    // If new format is used, return the URL directly
    if (profile.profile_url) {
      return profile.profile_url;
    }
    
    // Otherwise build the URL from the profile name
    return `https://instagram.com/${profile.instagram_profile}`;
  };

  // Function to toggle Smart Send
  const toggleSmartAdd = async () => {
    const newValue = !smartAddEnabled;
    setSmartAddEnabled(newValue);
    
    // Auto-save the setting
    if (!userId) return;
    
    try {
      const settingsRef = doc(firestore, `Whisper/${userId}/settings`, 'general');
      await setDoc(settingsRef, {
        profileNumber: profileNumber,
        smartAddEnabled: newValue
      }, { merge: true });
      
      setMessage("Smart Add setting updated!");
      setTimeout(() => setMessage(""), 2000);
    } catch (error) {
      console.error("Error saving Smart Add setting:", error);
      setMessage(`Error: ${error.message}`);
      // Revert the toggle if save failed
      setSmartAddEnabled(!newValue);
    }
  };

  // Get stats for header
  const getStats = () => {
    const totalProfiles = profiles.length;
    const activeProfiles = profiles.filter(p => p.status === 'active').length;
    const freshProfiles = profiles.filter(p => p.status === 'fresh').length;
    const pendingProfiles = profiles.filter(p => p.status === 'pending').length;
    
    return { totalProfiles, activeProfiles, freshProfiles, pendingProfiles };
  };

  const stats = getStats();

  return (
    <div className="whisper_container">
      {/* Header Section with Stats */}
      <div className="whisper_header_section">
        <div className="whisper_header_content">
          <div className="whisper_header_main">
            <div className="whisper_header_title_section">
              <h3 className="whisper_title">Instagram Agent</h3>
              <p className="whisper_subtitle">Manage your Instagram profiles and automation settings</p>
            </div>
            
            <div className="whisper_header_actions">
              <button 
                className="whisper_upload_button"
                onClick={toggleUploader}
              >
                <img src="/img/csv.svg" alt="upload" className="whisper_button_icon" />
                Upload CSV
              </button>
              <button 
                className="whisper_database_button"
                onClick={toggleDatabasePopup}
              >
                <img src="/img/settings.svg" alt="database" className="whisper_button_icon" />
                User ID
              </button>
              <button 
                className="whisper_database_button"
                onClick={toggleGeneralSettings}
              >
                <img src="/img/settings.svg" alt="settings" className="whisper_button_icon" />
                Settings
              </button>
            </div>
          </div>
          
          <div className="whisper_stats_section">
            <div className="whisper_stats_grid">
              <div className="whisper_stat_card">
                <div className="whisper_stat_number">{stats.totalProfiles}</div>
                <div className="whisper_stat_label">Total Profiles</div>
              </div>
              <div className="whisper_stat_card">
                <div className="whisper_stat_number">{stats.activeProfiles}</div>
                <div className="whisper_stat_label">Active</div>
              </div>
              <div className="whisper_stat_card">
                <div className="whisper_stat_number">{stats.freshProfiles}</div>
                <div className="whisper_stat_label">Fresh</div>
              </div>
              <div className="whisper_stat_card">
                <div className="whisper_stat_number">{stats.pendingProfiles}</div>
                <div className="whisper_stat_label">Pending</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Uploader Popup */}
      {showUploader && (
        <div className="whisper_popup_overlay" onClick={toggleUploader}>
          <div className="whisper_popup whisper_uploader_popup" onClick={(e) => e.stopPropagation()}>
            <div className="whisper_popup_header">
              <h4>Upload Instagram Profiles</h4>
              <button className="whisper_popup_close" onClick={toggleUploader}>×</button>
            </div>
            <div className="whisper_popup_content">
              <WhisperUploader userId={userId} />
            </div>
          </div>
        </div>
      )}
      
      {/* Database Path Popup */}
      {showDatabasePopup && (
        <div 
          className="whisper_popup_overlay"
          onClick={handlePopupOutsideClick}
        >
          <div className="whisper_popup">
            <div className="whisper_popup_header">
              <h4>User ID</h4>
              <button 
                className="whisper_popup_close" 
                onClick={toggleDatabasePopup}
              >
                ×
              </button>
            </div>
            <div className="whisper_popup_content">
              <p className="whisper_popup_description">
                Your Instagram User ID:
              </p>
              <div className="whisper_path_container">
                <input 
                  type="text" 
                  ref={databasePathRef}
                  value={userId}
                  readOnly
                  className="whisper_path_input"
                />
                <button 
                  onClick={copyUserId}
                  className="whisper_copy_button"
                >
                  Copy
                </button>
              </div>
              {copySuccess && <div className="whisper_copy_success">{copySuccess}</div>}
            </div>
          </div>
        </div>
      )}
      
      {/* Profiles Section */}
      <div className="whisper_section_wrapper">
        <div className="whisper_section_header">
          <div className="whisper_section_title">
            <div className="whisper_section_icon_container">
              <img src="/img/team.svg" alt="profiles" className="whisper_section_icon" />
            </div>
            Instagram Profiles ({profiles.length})
          </div>
        </div>
        
        <div className="whisper_section_content">
          {loading ? (
            <div className="whisper_loading_container">
              <div className="whisper_loading_spinner"></div>
              <p className="whisper_loading_message">Loading profiles...</p>
            </div>
          ) : (
            <>
              <div className="whisper_profiles_info">
                <div className="whisper_profiles_summary">
                  <p>There are <strong>{profiles.length}</strong> Instagram profiles available.</p>
                  
                  <div className="whisper_profiles_actions">
                    <button 
                      className="whisper_delete_all_button"
                      onClick={handleDeleteAllProfiles}
                      disabled={deletingAll || profiles.length === 0}
                    >
                      {deletingAll ? (
                        <>
                          <div className="whisper_button_loader"></div>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <img src="/img/bin.svg" alt="delete" className="whisper_button_icon" />
                          Delete All Entries
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="whisper_table_container">
                <table className="whisper_table">
                  <thead>
                    <tr>
                      <th>Instagram Profile</th>
                      <th>Link</th>
                      <th>Status</th>
                      <th>Account No.</th>
                      <th>Imported on</th>
                      <th>Source</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profiles.length === 0 ? (
                      <tr className="whisper_empty_table_row">
                        <td colSpan="7">
                          <div className="whisper_empty_table_content">
                            <div className="whisper_empty_table_icon">
                              <img src="/img/info.svg" alt="empty" />
                            </div>
                            <div className="whisper_empty_table_text">
                              <h4>No Instagram Profiles</h4>
                              <p>Your database is empty. Import profiles using the CSV upload button above.</p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      profiles.map((profile) => (
                        <tr key={profile.id} className="whisper_table_row">
                          <td>
                            <div className="whisper_profile_info">
                              <span className="whisper_profile_name">{profile.instagram_profile}</span>
                              {profile.raw_value && profile.raw_value !== profile.instagram_profile && (
                                <span className="whisper_original_value" title={`Original: ${profile.raw_value}`}>
                                  *
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <a 
                              href={getProfileUrl(profile)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="whisper_instagram_link"
                            >
                              <img src="/img/share_1.svg" alt="open" className="whisper_link_icon" />
                              Open
                            </a>
                          </td>
                          <td>
                            <span className={`whisper_status_badge whisper_status_${profile.status || 'unknown'}`}>
                              {profile.status || "Unknown"}
                            </span>
                          </td>
                          <td>
                            <span className="whisper_account_number">
                              {profile.accountNumber !== undefined ? profile.accountNumber : "-"}
                            </span>
                          </td>
                          <td>{formatDate(profile.import_date)}</td>
                          <td>{profile.source || "Unknown"}</td>
                          <td>
                            <div className="whisper_action_buttons">
                              <button 
                                className="whisper_view_button"
                                onClick={() => showProfileDetail(profile)}
                                title="View Details"
                              >
                                <img src="/img/tag_eye.svg" alt="view" />
                              </button>
                              <button 
                                className="whisper_delete_button"
                                onClick={() => handleDeleteProfile(profile.id)}
                                title="Delete Profile"
                              >
                                <img src="/img/bin.svg" alt="delete" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* General Settings Modal */}
      {showGeneralSettings && (
        <div className="whisper_popup_overlay" onClick={toggleGeneralSettings}>
          <div className="whisper_popup" onClick={(e) => e.stopPropagation()}>
            <div className="whisper_popup_header">
              <h4>General Settings</h4>
              <button className="whisper_popup_close" onClick={toggleGeneralSettings}>×</button>
            </div>
            <div className="whisper_popup_content">
              <div className="whisper_settings_grid">
                <div className="whisper_setting_group">
                  <div className="whisper_setting_header">
                    <label className="whisper_setting_label">
                      Profile Number
                    </label>
                    <span className="whisper_setting_description">
                      This number is used for Instagram profiles and can be freely chosen.
                    </span>
                  </div>
                  
                  <div className="whisper_setting_body">
                    <div className="whisper_input_wrapper">
                      <input
                        type="number"
                        value={profileNumber}
                        onChange={(e) => setProfileNumber(parseInt(e.target.value) || 0)}
                        min="0"
                        className="whisper_setting_input"
                        placeholder="Enter profile number..."
                      />
                    </div>
                  </div>
                </div>

                <div className="whisper_setting_group">
                  <div className="whisper_setting_header">
                    <label className="whisper_setting_label">
                      Smart Send
                    </label>
                    <span className="whisper_setting_description">
                      Enable Smart Send to automatically add new profiles.
                    </span>
                  </div>
                  
                  <div className="whisper_setting_body">
                    <div className="whisper_toggle_wrapper">
                      <input
                        type="checkbox"
                        checked={smartAddEnabled}
                        onChange={toggleSmartAdd}
                        className="whisper_toggle_checkbox"
                        id="smartAddToggle"
                      />
                      <label htmlFor="smartAddToggle" className="whisper_toggle">
                        <span className="whisper_toggle_slider"></span>
                      </label>
                      <span className="whisper_toggle_status">
                        {smartAddEnabled ? "On" : "Off"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="whisper_setting_actions">
                <button 
                  className="whisper_save_button" 
                  onClick={handleSaveSettings}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <div className="whisper_button_loader"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      Save Settings
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Profile Details Modal */}
      {showProfileDetails && selectedProfile && (
        <div className="whisper_modal_overlay" onClick={closeProfileDetails}>
          <div className="whisper_modal" onClick={(e) => e.stopPropagation()}>
            <div className="whisper_modal_header">
              <h4>Profile Details</h4>
              <button className="whisper_modal_close" onClick={closeProfileDetails}>×</button>
            </div>
            <div className="whisper_modal_content">
              <div className="whisper_profile_detail_grid">
                <div className="whisper_detail_item">
                  <label>Profile Name:</label>
                  <span>{selectedProfile.instagram_profile}</span>
                </div>
                <div className="whisper_detail_item">
                  <label>Status:</label>
                  <span className={`whisper_status_badge whisper_status_${selectedProfile.status || 'unknown'}`}>
                    {selectedProfile.status || "Unknown"}
                  </span>
                </div>
                <div className="whisper_detail_item">
                  <label>Account Number:</label>
                  <span>{selectedProfile.accountNumber !== undefined ? selectedProfile.accountNumber : "-"}</span>
                </div>
                <div className="whisper_detail_item">
                  <label>Source:</label>
                  <span>{selectedProfile.source || "Unknown"}</span>
                </div>
                <div className="whisper_detail_item">
                  <label>Imported on:</label>
                  <span>{formatDate(selectedProfile.import_date)}</span>
                </div>
                {selectedProfile.raw_value && selectedProfile.raw_value !== selectedProfile.instagram_profile && (
                  <div className="whisper_detail_item">
                    <label>Original Value:</label>
                    <span className="whisper_original_value">{selectedProfile.raw_value}</span>
                  </div>
                )}
              </div>
              <div className="whisper_modal_actions">
                <a 
                  href={getProfileUrl(selectedProfile)} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="whisper_modal_button whisper_modal_button_primary"
                >
                  <img src="/img/share_1.svg" alt="open" />
                  Open Profile
                </a>
                <button 
                  className="whisper_modal_button whisper_modal_button_danger"
                  onClick={() => {
                    handleDeleteProfile(selectedProfile.id);
                    closeProfileDetails();
                  }}
                >
                  <img src="/img/bin.svg" alt="delete" />
                  Delete Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {message && <div className="whisper_message">{message}</div>}
    </div>
  );
};

export default WhisperSettings; 